import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, Container, Typography, Card, CardContent, 
  Button, Chip, Stack, Accordion, AccordionSummary, AccordionDetails,
  TextField, InputAdornment, Tabs, Tab, Paper, alpha, Grid
} from '@mui/material';
import { 
  Phonelink, FlashOn, Code, ExpandMore, ArrowForward, 
  Search, HelpOutline, AutoAwesome, MonetizationOn, 
  Security, Speed, Layers, DeveloperMode, IntegrationInstructions 
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const faqData = [
  {
    category: "Architecture & Performance",
    question: "How does React Native achieve native UI performance?",
    answer: "React Native renders actual native iOS (UIKit) and Android (Android Views) UI components rather than running inside a web view. Coupled with the modern Hermes JavaScript engine and the Fabric renderer (New Architecture), execution overhead is minimized to deliver steady 60fps/120fps animations."
  },
  {
    category: "Architecture & Performance",
    question: "What is the new React Native Architecture (Fabric & TurboModules)?",
    answer: "The New Architecture replaces the legacy asynchronous JSON Bridge with Direct C++ JSI (JavaScript Interface) bindings. Fabric handles UI rendering with synchronous layout calculations, while TurboModules allow lazy-loading of native modules for faster cold start times."
  },
  {
    category: "Architecture & Performance",
    question: "How do you handle heavy computations without blocking the UI thread?",
    answer: "Heavy background processes (image processing, encryption, local DB querying) are offloaded to background threads using React Native Worklets, Web Workers, or custom C++/Kotlin/Swift native threads, ensuring the JavaScript main thread stays free for smooth user interaction."
  },
  {
    category: "Framework & Ecosystem",
    question: "Should I build with Expo or bare React Native CLI?",
    answer: "Expo is now the industry-recommended standard for almost all production React Native applications. With Expo Application Services (EAS) and custom Continuous Native Modules (CNMs), you get accelerated development workflows without sacrificing access to custom Swift or Kotlin code."
  },
  {
    category: "Framework & Ecosystem",
    question: "How much code can actually be shared between iOS, Android, and Web?",
    answer: "Typically, 85% to 95% of business logic, state management (Zustand/Redux), API service layers, and UI components are fully shared. Using React Native for Web, the exact same component codebase can also power desktop and web targets."
  },
  {
    category: "Framework & Ecosystem",
    question: "When should I choose React Native over Flutter?",
    answer: "React Native is ideal if your team has existing React/TypeScript expertise, requires seamless web-to-mobile code sharing, or prefers rendering actual platform-native UI widgets rather than custom canvas-painted elements."
  },
  {
    category: "Cost & Delivery",
    question: "What is the typical development timeline for a React Native MVP?",
    answer: "A feature-rich cross-platform MVP built with Expo and TypeScript typically ships in 6 to 10 weeks, including automated CI/CD setup, API integration, and test flight deployments."
  },
  {
    category: "Cost & Delivery",
    question: "How do Over-The-Air (OTA) updates work in React Native?",
    answer: "Using Expo Updates or CodePush, JavaScript bundle modifications and asset updates can be deployed directly to user devices instantly, bypassing standard App Store and Google Play store review queues for non-native hotfixes."
  },
  {
    category: "Security & Integration",
    question: "How is sensitive data stored on device in React Native?",
    answer: "Sensitive records, access tokens, and biometric credentials are encrypted at rest using iOS Keychain and Android KeyStore via secure modules like Expo SecureStore or react-native-encrypted-storage."
  }
];

const ReactNativeDevelopment = () => {
  const navigate = useNavigate();

  const siteUrl = "https://vikashtiwaridev.web.app/services/react-native";
  const pageTitle = "React Native App Development Services | Vikash Tiwari";
  <link rel="icon" type="image/x-icon" href="https://vikashtiwaridev.web.app/VikashTiwariIcon.png" />
  const pageDescription = "Cross-platform iOS and Android mobile app development with React Native and TypeScript. Native UI performance with shared JavaScript/TypeScript logic.";

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "React Native Mobile Development",
    "serviceType": "Mobile Application Development",
    "provider": {
      "@type": "Person",
      "name": "Vikash Tiwari",
      "url": "https://vikashtiwaridev.web.app/"
    },
    "areaServed": "Worldwide",
    "description": pageDescription
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('All');
  const [expandedFaq, setExpandedFaq] = useState(false);

  const categories = ['All', 'Architecture & Performance', 'Framework & Ecosystem', 'Cost & Delivery', 'Security & Integration'];

  const filteredFaqs = useMemo(() => {
    return faqData.filter((item) => {
      const matchesCategory = activeTab === 'All' || item.category === activeTab;
      const matchesSearch = item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            item.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, activeTab]);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? panel : false);
  };

  const capabilities = [
    { 
      title: "React Native & Expo Ecosystem", 
      desc: "Build cross-platform applications leveraging modern React Native components, EAS build pipelines, and Expo workflows for rapid product iteration.",
      icon: <Phonelink sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "TypeScript Architecture", 
      desc: "Strict type-safe client engineering ensuring compile-time bug elimination, reliable refactoring, and enterprise-grade maintainability.",
      icon: <Code sx={{ fontSize: 30, color: '#7C4DFF' }} />
    },
    { 
      title: "Native Module Integration", 
      desc: "Bridge custom Swift/Kotlin code via JSI (JavaScript Interface) when requiring low-level hardware interaction or specialized legacy SDKs.",
      icon: <IntegrationInstructions sx={{ fontSize: 30, color: '#00E5FF' }} />
    },
    { 
      title: "Performance & Memory Tuning", 
      desc: "Optimize Hermes engine execution, bundle size, re-render cycles, and list memory footprint for constant 60fps/120fps UI performance.",
      icon: <Speed sx={{ fontSize: 30, color: '#7C4DFF' }} />
    }
  ];

  const metrics = [
    { value: "90%+", label: "Shared Codebase" },
    { value: "<100ms", label: "Hermes Startup Time" },
    { value: "2x", label: "Delivery Velocity" },
    { value: "100%", label: "Native UI Components" }
  ];

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="React Native Developer, Mobile Developer India, Cross Platform App Engineer, TypeScript Mobile Apps, Expo Developer" />
        <link rel="canonical" href={siteUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={siteUrl} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Box component="main" sx={{ bgcolor: '#0A0E17', color: '#F1F5F9', minHeight: '100vh', overflowX: 'hidden' }}>
        
        {/* ================= HERO SECTION ================= */}
        <Box 
          sx={{ 
            position: 'relative', 
            pt: { xs: 10, md: 16 }, 
            pb: { xs: 10, md: 14 },
            background: 'radial-gradient(circle at 50% 10%, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0.08) 45%, rgba(10, 14, 23, 1) 100%)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute', top: '20%', left: '50%', transform: 'translateX(-50%)',
              width: { xs: '300px', md: '600px' }, height: { xs: '300px', md: '600px' },
              background: 'radial-gradient(circle, rgba(0, 229, 255, 0.12) 0%, rgba(124, 77, 255, 0) 70%)',
              filter: 'blur(80px)', pointerEvents: 'none'
            }} 
          />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ maxWidth: '880px', mx: 'auto', textAlign: 'center' }}>
              
              <Chip 
                icon={<AutoAwesome sx={{ fontSize: '18px !important', color: '#00E5FF' }} />} 
                label="Cross-Platform React Ecosystem" 
                sx={{ 
                  mb: 3, 
                  py: 2, 
                  px: 1,
                  bgcolor: alpha('#00E5FF', 0.08), 
                  color: '#00E5FF', 
                  border: '1px solid rgba(0, 229, 255, 0.3)',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  backdropFilter: 'blur(10px)'
                }} 
              />

              <Typography 
                variant="h1" 
                sx={{ 
                  fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.25rem' }, 
                  fontWeight: 900, 
                  lineHeight: 1.1,
                  mb: 3,
                  letterSpacing: '-0.02em'
                }}
              >
                Enterprise Grade{' '}
                <Box 
                  component="span" 
                  sx={{ 
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  React Native Development
                </Box>
              </Typography>

              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#94A3B8', 
                  fontSize: { xs: '1.1rem', md: '1.35rem' }, 
                  fontWeight: 400, 
                  lineHeight: 1.6, 
                  mb: 5,
                  maxWidth: '760px',
                  mx: 'auto'
                }}
              >
                Extend your web engineering investments to native iOS and Android. High-performance multi-platform applications engineered with TypeScript, Expo, and Native JSI Modules.
              </Typography>

              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={2} 
                justifyContent="center"
                sx={{ mb: 8 }}
              >
                <Button 
                  variant="contained" 
                  size="large" 
                  endIcon={<ArrowForward />} 
                  onClick={() => navigate('/contact')}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: '12px',
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #00E5FF 0%, #7C4DFF 100%)',
                    boxShadow: '0px 8px 24px rgba(0, 229, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #00D2EE 0%, #6B38FF 100%)',
                      boxShadow: '0px 12px 32px rgba(0, 229, 255, 0.45)',
                    }
                  }}
                >
                  Discuss React Native Project
                </Button>

                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => {
                    const el = document.getElementById('faq-section');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  sx={{ 
                    py: 1.8, 
                    px: 4, 
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: '12px',
                    textTransform: 'none',
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#F1F5F9',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      borderColor: '#00E5FF',
                      bgcolor: alpha('#00E5FF', 0.05)
                    }
                  }}
                >
                  View Technical FAQs
                </Button>
              </Stack>

              {/* Stats Bar */}
              <Grid container spacing={2}>
                {metrics.map((m, idx) => (
                  <Grid item xs={6} sm={3} key={idx}>
                    <Paper 
                      elevation={0}
                      sx={{ 
                        p: 2.5, 
                        borderRadius: '16px', 
                        bgcolor: 'rgba(15, 23, 42, 0.6)', 
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(12px)'
                      }}
                    >
                      <Typography 
                        variant="h4" 
                        sx={{ 
                          fontWeight: 800, 
                          background: 'linear-gradient(90deg, #00E5FF, #7C4DFF)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          mb: 0.5
                        }}
                      >
                        {m.value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#94A3B8', fontWeight: 500 }}>
                        {m.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>

            </Box>
          </Container>
        </Box>

        {/* ================= CORE CAPABILITIES (Strict 2x2 Grid with Correct Padding) ================= */}
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontSize: { xs: '2rem', md: '2.75rem' }, 
                fontWeight: 800, 
                mb: 2,
                letterSpacing: '-0.01em'
              }}
            >
              Core Engineering Capabilities
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '650px', mx: 'auto', fontSize: '1.1rem' }}>
              Scalable mobile architecture engineered to combine rapid release momentum with native performance.
            </Typography>
          </Box>

          {/* 2x2 CSS Grid Layout with Separated Spacing */}
          <Box 
            sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, 
              rowGap: { xs: 3, md: 4 },
              columnGap: { xs: 3, md: 4 }
            }}
          >
            {capabilities.map((cap, i) => (
              <Card 
                key={i}
                elevation={0}
                sx={{ 
                  height: '100%', 
                  borderRadius: '20px', 
                  bgcolor: 'rgba(15, 23, 42, 0.5)', 
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  p: 3, // Single source of padding on parent container
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-6px)',
                    borderColor: 'rgba(0, 229, 255, 0.4)',
                    boxShadow: '0 12px 30px rgba(0, 0, 0, 0.4)',
                    bgcolor: 'rgba(15, 23, 42, 0.8)'
                  }
                }}
              >
                <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                  <Box 
                    sx={{ 
                      width: 56, height: 56, borderRadius: '14px', 
                      bgcolor: 'rgba(255, 255, 255, 0.03)', 
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 3
                    }}
                  >
                    {cap.icon}
                  </Box>
                  <Typography variant="h5" sx={{ fontSize: '1.35rem', fontWeight: 700, mb: 1.5, color: '#F8FAFC' }}>
                    {cap.title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94A3B8', lineHeight: 1.7, fontSize: '0.975rem' }}>
                    {cap.desc}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Container>

        {/* ================= TECH STACK & ECOSYSTEM ================= */}
        <Box 
          sx={{ 
            py: { xs: 8, md: 10 }, 
            bgcolor: 'rgba(15, 23, 42, 0.7)', 
            borderTop: '1px solid rgba(255, 255, 255, 0.05)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
            background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.4) 0%, rgba(10, 14, 23, 0.8) 100%)'
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ textAlign: 'center', mb: 6 }}>
              <Typography variant="h3" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem' }, fontWeight: 800, mb: 2 }}>
                React Native Production Toolchain
              </Typography>
              <Typography variant="body1" sx={{ color: '#94A3B8' }}>
                Leveraging battle-tested libraries and modern state engines for enterprise stability.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} flexWrap="wrap" justifyContent="center" useFlexGap sx={{ gap: 1.5 }}>
              {[
                "React Native", "TypeScript", "Expo EAS", "Hermes Engine", "Fabric Renderer", 
                "Zustand / Redux Toolkit", "React Navigation", "Reanimated 3", "React Native Paper", 
                "AsyncStorage / MMKV", "Native Modules (JSI)", "Jest & React Native Testing Library", "Fastlane", "Sentry"
              ].map((tech) => (
                <Chip 
                  key={tech} 
                  label={tech} 
                  sx={{ 
                    py: 2.5, px: 2, 
                    fontSize: '0.95rem', 
                    fontWeight: 600,
                    borderRadius: '12px',
                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                    color: '#E2E8F0',
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha('#00E5FF', 0.1),
                      borderColor: '#00E5FF',
                      color: '#00E5FF'
                    }
                  }} 
                />
              ))}
            </Stack>
          </Container>
        </Box>

        {/* ================= TECHNICAL FAQ SECTION ================= */}
        <Container maxWidth="lg" id="faq-section" sx={{ py: { xs: 8, md: 12 } }}>
          
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip 
              icon={<HelpOutline sx={{ fontSize: '18px !important', color: '#7C4DFF' }} />} 
              label="Technical FAQs" 
              sx={{ mb: 2, bgcolor: alpha('#7C4DFF', 0.1), color: '#7C4DFF', fontWeight: 600 }} 
            />
            <Typography variant="h2" sx={{ fontSize: { xs: '2rem', md: '2.75rem' }, fontWeight: 800, mb: 2 }}>
              Frequently Asked Questions
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto' }}>
              Clear architectural answers regarding performance tuning, framework choices, and native module bridges.
            </Typography>
          </Box>

          {/* Search Bar & Category Filter */}
          <Box sx={{ maxWidth: '800px', mx: 'auto', mb: 5 }}>
            <TextField
              fullWidth
              placeholder="Search React Native FAQs (e.g., Hermes, Expo, performance, native modules)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#00E5FF' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'rgba(15, 23, 42, 0.8)',
                  color: '#F8FAFC',
                  fieldset: { borderColor: 'rgba(255, 255, 255, 0.15)' },
                  '&:hover fieldset': { borderColor: '#00E5FF' },
                  '&.Mui-focused fieldset': { borderColor: '#7C4DFF' }
                }
              }}
            />

            <Tabs 
              value={activeTab} 
              onChange={(e, newVal) => setActiveTab(newVal)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTabs-indicator': { backgroundColor: '#00E5FF', height: '3px', borderRadius: '3px' },
                '& .MuiTab-root': { 
                  color: '#94A3B8', 
                  fontWeight: 600, 
                  fontSize: '0.9rem',
                  textTransform: 'none',
                  '&.Mui-selected': { color: '#00E5FF' }
                }
              }}
            >
              {categories.map((cat) => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Box>

          {/* Accordions */}
          <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
            {filteredFaqs.length > 0 ? (
              filteredFaqs.map((faq, index) => {
                const panelId = `panel-${index}`;
                return (
                  <Accordion
                    key={index}
                    expanded={expandedFaq === panelId}
                    onChange={handleAccordionChange(panelId)}
                    sx={{
                      bgcolor: 'rgba(15, 23, 42, 0.6)',
                      color: '#F1F5F9',
                      mb: 2,
                      borderRadius: '14px !important',
                      border: '1px solid rgba(255, 255, 255, 0.08)',
                      backdropFilter: 'blur(10px)',
                      overflow: 'hidden',
                      '&:before': { display: 'none' },
                      '&.Mui-expanded': {
                        borderColor: 'rgba(0, 229, 255, 0.3)',
                        boxShadow: '0 8px 20px rgba(0,0,0,0.3)'
                      }
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMore sx={{ color: '#00E5FF' }} />}
                      sx={{ p: 2.5, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 600, pr: 2 }}>
                        {faq.question}
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 2.5, pt: 0, color: '#94A3B8', lineHeight: 1.7 }}>
                      <Typography variant="body2" sx={{ fontSize: '0.975rem' }}>
                        {faq.answer}
                      </Typography>
                      <Chip 
                        label={faq.category} 
                        size="small" 
                        sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#64748B', fontSize: '0.75rem' }} 
                      />
                    </AccordionDetails>
                  </Accordion>
                );
              })
            ) : (
              <Box sx={{ textAlign: 'center', py: 6, color: '#64748B' }}>
                <Typography variant="h6">No matching questions found.</Typography>
                <Typography variant="body2">Try searching for alternative topics or select "All".</Typography>
              </Box>
            )}
          </Box>

        </Container>

        {/* ================= CALL TO ACTION BANNER ================= */}
        <Container maxWidth="lg" sx={{ pb: { xs: 8, md: 12 } }}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 5, md: 8 },
              borderRadius: '28px',
              background: 'linear-gradient(135deg, rgba(0, 229, 255, 0.15) 0%, rgba(124, 77, 255, 0.15) 100%)',
              border: '1px solid rgba(0, 229, 255, 0.25)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            <Typography variant="h3" sx={{ fontSize: { xs: '1.8rem', md: '2.5rem' }, fontWeight: 800, mb: 2 }}>
              Ready to Accelerate Your Mobile Product?
            </Typography>
            <Typography variant="body1" sx={{ color: '#94A3B8', maxWidth: '600px', mx: 'auto', mb: 4, fontSize: '1.1rem' }}>
              Schedule a technical scoping session to plan your React Native application, evaluate Expo vs Native modules, and define delivery timelines.
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/contact')}
              sx={{
                py: 1.8,
                px: 5,
                fontSize: '1.05rem',
                fontWeight: 700,
                borderRadius: '12px',
                textTransform: 'none',
                background: '#F8FAFC',
                color: '#0A0E17',
                '&:hover': {
                  background: '#FFFFFF',
                  boxShadow: '0px 8px 24px rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Get Started
            </Button>
          </Paper>
        </Container>

      </Box>
    </>
  );
};

export default ReactNativeDevelopment;